/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState, useCallback } from "react";
import {
  fetchTopics as loadTopics,
  fetchTwoLevels as loadTwoLevels,
} from "../utils/topics";
import { LabelValue, findProductByLabel } from "../utils/tickets";
import { productData } from "settings/constants/constants";

export type TopicRequestType = "Change Request" | "Incident Report";

export interface UseTopicsLoaderProps {
  api: any;
  globalProduct: string;
  isKontrolEdge: boolean;
  /** Whose topic list to load. Only Change Request has a second level. */
  requestType?: TopicRequestType;
  /** Product label → the value the topics endpoint expects. */
  products?: LabelValue[];
}

export interface UseTopicsLoaderReturn {
  topics: LabelValue[];
  selectedTopicLabel: string;
  twoLevelOptions: LabelValue[];
  selectedTwoLevelLabel: string;
  loadingTopics: boolean;
  loadingTwoLevels: boolean;
  setSelectedTopicLabel: (label: string) => void;
  setSelectedTwoLevelLabel: (label: string) => void;
  handleSelectTopic: (topicLabel: string) => Promise<void>;
  resetTopics: () => void;
}

/**
 * Topics (and, for Change Request, their second level) of the CTX Reflector
 * request modals. Kontrol Edge tickets have no topics, so nothing loads there.
 */
export const useTopicsLoader = ({
  api,
  globalProduct,
  isKontrolEdge,
  requestType = "Change Request",
  products = productData,
}: UseTopicsLoaderProps): UseTopicsLoaderReturn => {
  const [topics, setTopics] = useState<LabelValue[]>([]);
  const [selectedTopicLabel, setSelectedTopicLabel] = useState("");
  const [twoLevelOptions, setTwoLevelOptions] = useState<LabelValue[]>([]);
  const [selectedTwoLevelLabel, setSelectedTwoLevelLabel] = useState("");
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [loadingTwoLevels, setLoadingTwoLevels] = useState(false);

  useEffect(() => {
    if (isKontrolEdge) return;
    const found = findProductByLabel(products, globalProduct);
    if (!found) return;

    let alive = true;
    setLoadingTopics(true);

    loadTopics(api, requestType, found.value)
      .then((d) => {
        if (alive) setTopics(d);
      })
      .catch((err) => console.error("loadTopics error:", err))
      .finally(() => {
        if (alive) setLoadingTopics(false);
      });

    return () => {
      alive = false;
      // The product in session starts as "CTX Reflector" and switches once
      // the profile loads. That cancels this load mid-flight, and since the
      // `finally` above skips a cancelled load, nothing else would ever turn
      // the flag off: the modal stayed on "Processing request…" (CTX-1256).
      setLoadingTopics(false);
    };
  }, [api, globalProduct, isKontrolEdge, requestType, products]);

  const handleSelectTopic = useCallback(
    async (topicLabel: string) => {
      setSelectedTopicLabel(topicLabel);
      setTwoLevelOptions([]);
      setSelectedTwoLevelLabel("");
      if (requestType !== "Change Request") return;

      const productFound = findProductByLabel(products, globalProduct);
      if (!productFound) return;

      setLoadingTwoLevels(true);
      try {
        const opts = await loadTwoLevels(
          api,
          "Change Request",
          productFound.value,
          topicLabel
        );
        setTwoLevelOptions(opts);
      } catch (e) {
        console.error("loadTwoLevels:", e);
      } finally {
        setLoadingTwoLevels(false);
      }
    },
    [api, globalProduct, requestType, products]
  );

  // Clears what the user picked, not the topic list: the list depends only on
  // the product, and the effect above does not run again when the modal
  // reopens, so wiping it left Topic empty from the second ticket on.
  const resetTopics = useCallback(() => {
    setSelectedTopicLabel("");
    setTwoLevelOptions([]);
    setSelectedTwoLevelLabel("");
  }, []);

  return {
    topics,
    selectedTopicLabel,
    twoLevelOptions,
    selectedTwoLevelLabel,
    loadingTopics,
    loadingTwoLevels,
    setSelectedTopicLabel,
    setSelectedTwoLevelLabel,
    handleSelectTopic,
    resetTopics,
  };
};
